import { Formatter, useCMSFeatureds } from "@with-nx/hooks-n-helpers";
import { useServerRegion } from "@with-nx/region";
import {
  DesignedButton,
  DesignedCard,
  ReusableCustomerServiceContainer,
  ReusableSection,
  Rule,
} from "@with-nx/simple-ui/atoms";
import { BlueUnderline, VisibleContainer } from "@with-nx/simple-ui/molecules";
import {
  CatalogLayout,
  GradientHolder,
  GridStepScroller,
  ReusableDoubleGradientCard,
  ReusableSlantedContainer,
  SolutionsHero,
  SolutionsPartnerShowcase,
  SolutionsSearch,
  SolutionsStories,
  SolutionsTestimonials,
} from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";
import { useRouter } from "next/router";
import { useState } from "react";
import { Box } from "simple-effing-primitive-layout";

import ReusableSearchSection from "../../components/w/ReusableSearchSection";
export const getServerSideProps = async ({ resolvedUrl }) => {
  return {
    props: {
      resolvedUrl,
    },
  };
};
export default function Page({ resolvedUrl }) {
  const featureds = useCMSFeatureds({});
  const region = useServerRegion();

  const _featureds = featureds.featureds.filter((i) => {
    return (
      i?.partnerTags?.includes("EDtA") &&
      i?.regionTags?.includes(region?.id?.toString() || "")
    );
  });
  const router = useRouter();
  const [all, _all] = useState(false);

  return (
    <CatalogLayout
      transparentFixedNavBar
      title="Broadway Media - School Theatre"
      description="We know, you're currently wearing five hats and you need help. Welcome to Broadway Media - we're your production experts and here to help with solutions that will elevate your production"
      resolvedUrl={resolvedUrl}
    >
      <SolutionsHero
        title="Make Your Stage Come Alive!"
        description="Simplify your show with streamlined production solutions tailored for busy educators."
        buttons={["request-information", "schedule-a-call"]}
        src="/assets/solution/resources-for-school-theatre-provided-by-broadway-media.png"
        sponsors={["edta"]}
      />
      <ReusableSlantedContainer spotlights>
        <SolutionsStories filter="School Theatre" minimal />
      </ReusableSlantedContainer>
      <ReusableSection>
        <div className="container">
          <Row gutter={[36, 18]} align="middle">
            <Col xs={24}>
              <Rule
                parse="!hl d:block mb:12"
                css="--blue-gradient-text"
                tag="h3"
              >
                Elevate Your Production with Scenic Projections & Choreography
                Guides!
              </Rule>
              <Rule parse="!pl d:block mb:12 c:?font2">
                At Broadway Media, trust and reliability are at the core of our
                business. We&apos;ve established partnerships with Music Theatre
                International, Disney Theatrical, Broadway Licensing and
                Theatrical Rights Worldwide, making us the most trusted resource
                for school theatre resources across the globe. Join industry
                leaders and experience the transformative power of our resources
                firsthand.
              </Rule>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Rule parse="!ht d:block mb:12 c:?font2" tag="p">
                From Broadway to the Classroom
              </Rule>
              <Rule parse="!pl d:block mb:12 c:?font3">
                Scenic Projections for school theatre are revolutionizing the
                way students connect with digital scenery in the performing
                arts. Developed in collaboration with Music Theatre
                International, Disney, Broadway Junior, and TRW&apos;s
                Young@Part, we offer affordable scenery solutions that enhance
                and elevate your productions.
              </Rule>
              <Rule parse="!pl d:block mb:36 c:?font3">
                Why choose our Scenic Projections? As the only authorized source
                for digital scenery, we guarantee script accuracy. This core
                product value translates to flawless integration with your
                script and rehearsal process. Scenic Projections, you&apos;ll
                have access to each scene, setting, and animated magical moment
                from the script, all without the significant expense associated
                with renting hand-painted backdrops.
              </Rule>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <DesignedCard
                image="/assets/section/school-theatre-1.webp"
                size="16x9"
              />
            </Col>
            <Col xs={24} sm={24} md={12}>
              <DesignedCard
                image="/assets/section/school-theatre-2.webp"
                size="16x9"
              />
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Rule parse="!ht d:block mb:12 c:?font2" tag="p">
                Step into Excellence
              </Rule>
              <Rule parse="!pl d:block mb:36 c:?font3">
                Introducing our Choreography Guides - your ultimate tool for
                bolstering performer confidence and elevating your shows.
                Curated by Tony-winning director/choreographer Jerry Mitchell
                and Broadway dancer Paul Canaan, these user-friendly digital
                videos cater to performers across all age groups and expertise.
                Immerse yourself in the narratives behind each move and derive
                invaluable insights straight from the Broadway visionaries. With
                Choreography Guides at your side, effortlessly merge Broadway
                quality choreography into your creative vision, ensuring
                audience satisfaction.
              </Rule>
            </Col>
          </Row>
        </div>
      </ReusableSection>
      {_featureds.length > 0 ? (
        <ReusableSection>
          <div className="container">
            <Rule parse="!hl d:block mb:36">New Releases</Rule>
            <GridStepScroller
              xs={24}
              sm={24}
              md={8}
              items={[
                ..._featureds.map((i, ii) => {
                  if (!all && ii >= 3) {
                    return null;
                  }

                  if (all && ii >= 9) {
                    return null;
                  }

                  return (
                    <GradientHolder
                      src={Formatter.image(i.image, true)}
                      key={i._id}
                      render={(colors) => (
                        <ReusableDoubleGradientCard
                          key={i._id}
                          gradients={
                            colors.ready
                              ? [colors.hex, colors.hex]
                              : ["transparent", "transparent"]
                          }
                          title={i.name}
                          holder={false}
                          press={() => {
                            router.push(i.url);
                          }}
                          properties={{ height: 256 }}
                        >
                          <Box parse="p:absolute t:0 l:0 w:100% h:100% d:flex a:center j:center">
                            {colors.ready ? (
                              <img
                                loading="lazy"
                                width="176"
                                height="176"
                                src={colors.src}
                                alt={i.name || "Broadway Media"}
                              />
                            ) : undefined}
                          </Box>
                        </ReusableDoubleGradientCard>
                      )}
                    />
                  );
                }),
              ]}
            />
            {all ? undefined : (
              <Box parse="d:flex j:flex-end mt:24">
                <DesignedButton
                  size="small"
                  theme="tetriary"
                  label="View All Shows"
                  press={() => {
                    _all(true);
                  }}
                />
              </Box>
            )}
          </div>
        </ReusableSection>
      ) : undefined}
      <SolutionsPartnerShowcase name="EdTA" />
      <SolutionsTestimonials title="Trusted by Teachers" titleTag="h3" />
      <ReusableCustomerServiceContainer />
      <VisibleContainer>
        <SolutionsSearch
          title={
            <Box parse="d:flex a:baseline j:flex-start p:relative oy:visible mb:24 fw:wrap">
              <Rule parse="d:flex" tag="h5">
                <Rule
                  parse="!hl d:inline-block"
                  display="block"
                  bottom={12}
                  right={12}
                  tag="span"
                >
                  We&apos;re committed to resources that
                </Rule>
                <Box parse="p:relative">
                  <Rule
                    parse="!hl d:inline-block p:relative"
                    display="block"
                    css="--blue-gradient-text"
                    bottom={0}
                    style={{ marginBottom: 0 }}
                    tag="span"
                  >
                    elevate
                    <BlueUnderline />
                  </Rule>
                </Box>
                <Rule
                  parse="!hl d:inline-block"
                  display="block"
                  bottom={12}
                  left={12}
                  tag="span"
                >
                  your performance.
                </Rule>
              </Rule>
            </Box>
          }
          description="Unlock your creativity, simplify your process, and create unforgettable performances that leave lasting impressions."
        >
          <ReusableSearchSection productCategory="global" />
        </SolutionsSearch>
      </VisibleContainer>
    </CatalogLayout>
  );
}
